import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import WorkbookUpload, { ImportSummary } from '../maintenance-analysis/WorkbookUpload';
import { toSystemRecords } from '../maintenance-analysis/analysis';
import { monthLabel } from '../maintenance-analysis/charts';
import Chart from 'chart.js/auto';
import { toast } from 'react-toastify';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import AppShell from '../../components/layout/AppShell';
import { PageHeader, Button, Card, CardHeader, CardBody, Modal, Input, Textarea, FieldSet, EmptyState } from '../../components/ui';
import { analyze, norm, fmtPct } from './analyzer';
import { downloadSystemReportPdf } from './exportPdf';
import './systemReport.css';

const emptyCharts = () => ({
  activityTimeline: { labels: [], pm: [], cm: [] },
  faultCategories: { labels: [], values: [] },
  cmMaterials: { labels: [], values: [] },
  rackCoverage: { labels: [], values: [] },
  correctiveActions: { labels: [], values: [] },
});

// Keep general report details prefilled; monthly results come from the workbook or manual entry.
const initialReport = () => ({
  reportMonth: '', site: 'Hamad International Airport', coverage: 'Concourse A, B, C, and PTC',
  components: 'Controllers, Mainframes, Module Devices, Mic Stations, Speakers',
  purpose: 'Provide efficient Public Address system',
  activeZones: '', devicesOnline: '', connectivity: '',
  connectivityNote: '', uptime: '',
  errors: '', warnings: '', alerts: '',
  preventiveChecks: '',
  correctiveActions: '', pendingTasks: '',
  rec1: 'Upgrade obsolete items to maintain efficiency',
  rec2: 'Stable network is mandatory to maintain continuous connections and reduce downtime',
  rec3: 'Keep spares aligned with the current replacement pattern.',
  preparedBy: 'RGA', generatedDate: new Date().toISOString().slice(0, 10),
  pmRecords: [], cmRecords: [],
  files: { pm: '', cm: '', cmitem: '' },
  summaries: { pm: 'Awaiting workbook.', cm: 'Awaiting workbook.', cmitem: 'No materials sheet supplied.' },
  charts: emptyCharts(),
});

const CONTENT_FIELDS = ['reportMonth', 'site', 'coverage', 'components', 'purpose', 'activeZones', 'devicesOnline',
  'connectivity', 'connectivityNote', 'uptime', 'errors', 'warnings', 'alerts', 'preventiveChecks',
  'correctiveActions', 'pendingTasks', 'rec1', 'rec2', 'rec3', 'preparedBy'];
const NUMERIC_FIELDS = ['activeZones', 'errors', 'warnings', 'alerts'];

const EDITOR_SECTIONS = [
  { legend: 'Report settings', fields: [['reportMonth', 'Report month'], ['site', 'Site'], ['coverage', 'Coverage'], ['preparedBy', 'Prepared by'], ['generatedDate', 'Generated date', 'date']] },
  { legend: 'System narrative', fields: [['components', 'Core components', 'textarea'], ['purpose', 'Purpose', 'textarea']] },
  { legend: 'Operational metrics', fields: [['activeZones', 'Active controllers'], ['devicesOnline', 'Devices online'], ['uptime', 'System uptime'], ['connectivity', 'Connectivity'], ['connectivityNote', 'Connectivity note'], ['errors', 'Errors'], ['warnings', 'Warnings'], ['alerts', 'Alerts']] },
  { legend: 'Maintenance details', fields: [['preventiveChecks', 'Preventive checks', 'textarea'], ['correctiveActions', 'Corrective actions', 'textarea'], ['pendingTasks', 'Pending tasks', 'textarea']] },
  { legend: 'Recommendations', fields: [['rec1', 'Recommendation 1'], ['rec2', 'Recommendation 2'], ['rec3', 'Recommendation 3']] },
];


const pct = (v) => { const n = parseFloat(String(v || '').replace(/%/g, '')); return Number.isNaN(n) ? 0 : Math.min(Math.max(n, 0), 100); };
const intVal = (v) => Math.max(0, Math.round(parseFloat(String(v || '').replace(/[^0-9.-]/g, '')) || 0));
const fmtDate = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : v || '';
};

/* ---- charts ---- */

const TICKS = { color: '#56718f', font: { size: 11, weight: '600' } };
const GRID = { color: 'rgba(148,163,184,.18)' };
const baseOptions = (extra) => ({
  responsive: true, maintainAspectRatio: false, animation: false,
  plugins: { legend: { display: false }, tooltip: { backgroundColor: '#0f2742', padding: 10, cornerRadius: 10 } },
  ...extra,
});
const legend = (position) => ({ display: true, position, labels: { color: '#35506b', usePointStyle: true, boxWidth: 8, padding: 12, font: { size: 11, weight: '700' } } });
const truncate = (t, max) => (max && t.length > max ? `${t.slice(0, max - 3)}...` : t);

const hbar = (s, color, maxLabelChars = 0) => s.labels.length ? {
  type: 'bar',
  data: { labels: s.labels, datasets: [{ data: s.values, backgroundColor: color, borderRadius: 12, borderSkipped: false, barThickness: 14 }] },
  options: baseOptions({
    indexAxis: 'y',
    scales: {
      x: { beginAtZero: true, ticks: { ...TICKS, precision: 0 }, grid: GRID },
      y: { grid: { display: false }, ticks: { color: '#35506b', font: { size: 11, weight: '700' }, callback(v) { return truncate(norm(this.getLabelForValue(v)), maxLabelChars); } } },
    },
  }),
} : null;

function buildChartConfigs(c) {
  const tl = c.activityTimeline;
  const line = (label, data, color, fill) => ({ label, data, borderColor: color, backgroundColor: fill, fill: true, tension: 0.35, borderWidth: 2.5, pointRadius: 3, pointBackgroundColor: color });
  return {
    activity: tl.labels.length ? {
      type: 'line',
      data: { labels: tl.labels, datasets: [line('PM', tl.pm, '#1d7fd6', 'rgba(29,127,214,.15)'), line('CM', tl.cm, '#7ba402', 'rgba(123,164,2,.12)')] },
      options: baseOptions({
        plugins: { legend: legend('top') },
        scales: { x: { grid: { display: false }, ticks: { ...TICKS, maxRotation: 0 } }, y: { beginAtZero: true, ticks: { ...TICKS, precision: 0 }, grid: GRID } },
      }),
    } : null,
    corrective: c.correctiveActions.values.some((v) => v > 0) ? {
      type: 'doughnut',
      data: { labels: c.correctiveActions.labels, datasets: [{ data: c.correctiveActions.values, backgroundColor: ['#1d7fd6', '#7ba402', '#f5a623', '#ea5f4d', '#6c7f99'], borderColor: '#f7fbff', borderWidth: 3 }] },
      options: baseOptions({ cutout: '62%', plugins: { legend: legend('bottom') } }),
    } : null,
    faults: hbar(c.faultCategories, '#0f63ad'),
    materials: hbar(c.cmMaterials, '#1d7fd6', 26),
    racks: hbar(c.rackCoverage, '#f5a623'),
  };
}

function ReportChart({ config }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!config) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return config ? <canvas ref={ref} /> : <p className="text-sm text-muted-fg">No data.</p>;
}

/* ---- report sheet pieces ---- */

const Section = ({ title, className = '', children }) => (
  <Card className={`min-w-0 flex flex-col break-inside-avoid ${className}`}>
    <CardHeader title={title} />
    <CardBody className="flex-1">{children}</CardBody>
  </Card>
);

const Label = ({ name, children }) => (
  <li className="flex gap-2 text-sm leading-relaxed text-foreground">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
    <span><b className="font-semibold">{name}:</b> {children || '—'}</span>
  </li>
);

const Band = ({ label, value, note, tone }) => (
  <div className={`rounded-md px-4 py-3 ${tone}`}>
    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
    <p className="tabular text-xl font-semibold">{value || '—'}</p>
    {note && <p className="mt-0.5 text-xs opacity-80">{note}</p>}
  </div>
);

const FAULTS = [['errors', 'Errors', 'bg-danger'], ['warnings', 'Warnings', 'bg-warning'], ['alerts', 'Alerts', 'bg-info']];

function ReportSheet({ r, chartConfigs, exportMode = false }) {
  const uptime = pct(r.uptime);
  const counts = FAULTS.map(([key]) => intVal(r[key]));
  const maxCount = Math.max(...counts, 1);
  const recs = [r.rec1, r.rec2, r.rec3].filter((x) => norm(x));
  const hasCharts = Object.values(chartConfigs).some(Boolean);

  return (
    <div id={exportMode ? undefined : "system-report"} className={`flex flex-col gap-4${exportMode ? ' rga-pdf-export' : ''}`}>
      <div data-pdf-page className="flex flex-col gap-4 p-1">
      <header style={exportMode ? { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' } : undefined} className="flex flex-col gap-2 rounded-lg bg-primary px-5 py-4 text-primary-fg sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-primary-fg">Public Address System</h2>
          <p className="text-sm opacity-80">System Report{norm(r.reportMonth) && ` (${r.reportMonth})`}</p>
        </div>
        <div className="text-sm sm:text-right">
          {norm(r.site) && <p><b>Site:</b> {r.site}</p>}
          {r.generatedDate && <p><b>Generated:</b> {fmtDate(r.generatedDate)}</p>}
        </div>
      </header>

      <div style={exportMode ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined} className="grid grid-cols-1 gap-4 lg:grid-cols-3 print:grid-cols-3">
        <Section title="System overview">
          <ul className="space-y-2">
            <Label name="Site">{r.site}</Label>
            <Label name="Coverage">{r.coverage}</Label>
            <Label name="Components">{r.components}</Label>
            <Label name="Purpose">{r.purpose}</Label>
          </ul>
        </Section>
        <Section title="Operational status">
          <div className="space-y-2">
            <Band label="Active controllers" value={r.activeZones} tone="bg-info-soft text-info-fg" />
            <Band label="Devices online" value={r.devicesOnline} tone="bg-success-soft text-success-fg" />
            <Band label="Connectivity" value={r.connectivity} note={r.connectivityNote} tone="bg-neutral-soft text-neutral-fg" />
          </div>
        </Section>
        <Section title="Maintenance records">
          <ul className="space-y-2">
            <Label name="Preventive checks">{r.preventiveChecks}</Label>
            <Label name="Corrective actions">{r.correctiveActions}</Label>
            <Label name="Pending tasks">{r.pendingTasks}</Label>
          </ul>
        </Section>

        <Section title="Performance metrics">
          <div className="flex items-center justify-center gap-6">
            <div className="relative h-32 w-32">
              <svg width="128" height="128" viewBox="0 0 120 120" className="h-full w-full" aria-hidden="true">
                <g transform="rotate(-90 60 60)">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke="#0d5ca6" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray="326.73" strokeDashoffset={326.73 * (1 - uptime / 100)} />
                </g>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="tabular text-xl font-semibold text-foreground">{uptime > 0 ? fmtPct(uptime) : '--'}</span>
                <span className="text-xs text-muted-fg">System uptime</span>
              </div>
            </div>
          </div>
        </Section>
        <Section title="Fault logs">
          <div className="flex h-36 items-end justify-around gap-4">
            {FAULTS.map(([key, label, color], i) => (
              <div key={key} className="flex h-full flex-1 flex-col items-center gap-2">
                <span className="tabular h-5 shrink-0 text-sm leading-5 font-semibold text-foreground">{norm(r[key]) ? counts[i] : '—'}</span>
                <div className="flex w-8 flex-1 items-end rounded-md bg-muted">
                  <div className={`w-full rounded-md ${color}`} style={{ height: `${Math.round((counts[i] / maxCount) * 100)}%` }} />
                </div>
                <span className="text-xs text-muted-fg">{label}</span>
              </div>
            ))}
          </div>
        </Section>
        {recs.length > 0 && (
          <Section title="Recommendations">
            <ul className="space-y-2">
              {recs.map((rec) => (
                <li key={rec} className="flex gap-2 text-sm leading-relaxed text-foreground">
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-success" aria-hidden="true" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      </div>
      <div data-pdf-page className="flex flex-col gap-4 p-1">
      {hasCharts && (
        <>
          <div style={exportMode ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined} className="grid grid-cols-1 gap-4 lg:grid-cols-2 print:grid-cols-2">
            <Section title="Maintenance activity timeline"><div className="h-64"><ReportChart config={chartConfigs.activity} /></div></Section>
            <Section title="Corrective action mix"><div className="h-64"><ReportChart config={chartConfigs.corrective} /></div></Section>
          </div>
          <div style={exportMode ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined} className="grid grid-cols-1 gap-4 lg:grid-cols-3 print:grid-cols-3">
            <Section title="Fault category distribution"><div className="h-64"><ReportChart config={chartConfigs.faults} /></div></Section>
            <Section title="CM materials used"><div className="h-64"><ReportChart config={chartConfigs.materials} /></div></Section>
            <Section title="Rack coverage"><div className="h-64"><ReportChart config={chartConfigs.racks} /></div></Section>
          </div>
        </>
      )}

      {norm(r.preparedBy) && <footer className="text-right text-sm text-muted-fg">Prepared: <b className="text-foreground">{r.preparedBy}</b></footer>}
      </div>
    </div>
  );
}

/* ---- page ---- */

function SystemReportPage() {
  const location = useLocation();
  const [workbook, setWorkbook] = useState(location.state?.maintenanceWorkbook || null);
  const [report, setReport] = useState(() => {
    const data = location.state?.maintenanceWorkbook;
    if (!data) return initialReport();
    const records = toSystemRecords(data);
    const { charts, pmSummary, cmSummary, ...fields } = analyze(records);
    return { ...initialReport(), ...records, ...fields, reportMonth: monthLabel(data.month),
      charts: { ...charts, cmMaterials: { labels: [], values: [] } } };
  });
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const chartConfigs = useMemo(() => buildChartConfigs(report.charts), [report.charts]);
  const hasContent = report.pmRecords.length > 0 || report.cmRecords.length > 0 || CONTENT_FIELDS.some((f) => norm(report[f]));

  const setField = (field, value) =>
    setReport((r) => ({ ...r, [field]: NUMERIC_FIELDS.includes(field) ? value.replace(/[^0-9]/g, '') : value }));

  const importWorkbook = (data) => {
    toast.success(`Report filled for ${monthLabel(data.month)}: ${data.cm.length} CM and ${data.pm.length} PPM records. Click Done to preview.`);
    setWorkbook(data);
    setReport((r) => {
      const records = toSystemRecords(data);
      const { charts, pmSummary, cmSummary, ...fields } = analyze(records);
      return { ...r, ...records, ...fields, reportMonth: monthLabel(data.month),
        charts: { ...charts, cmMaterials: { labels: [], values: [] } },
        files: { pm: data.filename, cm: data.filename, cmitem: '' },
        summaries: { pm: pmSummary, cm: cmSummary, cmitem: 'Materials data is not included in the CM / PPM workbook.' } };
    });
  };

  const exportPdf = async () => {
    setExporting(true);
    const host = document.createElement('div');
    Object.assign(host.style, { position: 'absolute', left: '-10000px', top: '0', width: '1120px', background: '#FFFFFF' });
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
    const root = createRoot(host);
    try {
      await document.fonts?.ready;
      const exportCharts = buildChartConfigs(report.charts);
      Object.values(exportCharts).filter(Boolean).forEach((config) => { config.options.devicePixelRatio = 3; });
      flushSync(() => root.render(<ReportSheet r={report} chartConfigs={exportCharts} exportMode />));
      await downloadSystemReportPdf(host, report.reportMonth);
    } catch (error) { toast.error(error.message || 'PDF export failed. Please try again.'); }
    finally { root.unmount(); host.remove(); setExporting(false); }
  };

  return (
    <>
      {/* Mount the default page rule only on this route so browser PDF export uses landscape. */}
      <style media="print">{'@page { size: A4 landscape; margin: 8mm; }'}</style>
      <div className="print:hidden">
        <PageHeader
          title="System report"
          subtitle="Public Address System report from a CM / PPM workbook"
          backTo="/"
          actions={
            <>
              <Button variant="secondary" icon={EditOutlined} onClick={() => setEditing(true)}>Edit report</Button>
              <Button icon={PictureAsPdfOutlined} onClick={exportPdf} disabled={!hasContent || exporting}>{exporting ? 'Preparing PDF…' : 'Export PDF'}</Button>
            </>
          }
        />
      </div>

      <AppShell>
        {hasContent ? (
          <ReportSheet r={report} chartConfigs={chartConfigs} />
        ) : (
          <EmptyState title="No report data yet" description="Click Edit report to upload a CM / PPM workbook or enter values manually." />
        )}
      </AppShell>

      <Modal
        isOpen={editing}
        onClose={() => setEditing(false)}
        title="Report editor"
        subtitle="Upload one CM / PPM Excel workbook and fill in the report fields."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setReport(initialReport()); setWorkbook(null); toast.info('Report reset.'); }}>Reset</Button>
            <Button onClick={() => setEditing(false)}>Done</Button>
          </>
        }
      >
        <div className="space-y-8">
          <FieldSet legend="Data import">
            <WorkbookUpload onLoaded={importWorkbook} />
            <div className="mt-4"><ImportSummary data={workbook} /></div>
            <p className="mt-2 text-sm text-muted-fg">The CM / PPM workbook supplies maintenance records. Materials usage requires separate source data and is not inferred.</p>
          </FieldSet>

          {EDITOR_SECTIONS.map(({ legend: title, fields }) => (
            <FieldSet key={title} legend={title}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {fields.map(([field, label, kind]) => {
                  const props = { label, value: report[field] || '', onChange: (e) => setField(field, e.target.value) };
                  return kind === 'textarea'
                    ? <Textarea key={field} rows={2} className="sm:col-span-2" {...props} />
                    : <Input key={field} type={kind === 'date' ? 'date' : 'text'} inputMode={NUMERIC_FIELDS.includes(field) ? 'numeric' : undefined} {...props} />;
                })}
              </div>
            </FieldSet>
          ))}
        </div>
      </Modal>
    </>
  );
}

export default SystemReportPage;
